import Vue from "vue"
import {watch} from "@/store/store"
import Portal from "@/managers/portal"
import Locations from "@/managers/location/locations"
import ContentSubscription from "@/managers/data/location/contentSubscription"
import Notifications from "@/managers/session/notifications"

class ContentSubscriptions {
	private readonly state: {map: Map<number, ContentSubscription> | null}

	public get isReady(): boolean {
		return this.state.map !== null
	}

	public get hasAny(): boolean {
		return this.isReady && this.map.size > 0
	}

	public get list(): ContentSubscription[] {
		if (this.state.map === null)
			throw new Error("Content subscriptions are not ready")

		return Array.from(this.state.map.values())
	}

	public get map(): Map<number, ContentSubscription> {
		if (this.state.map === null)
			throw new Error("Content subscriptions are not ready")

		return this.state.map
	}

	constructor() {
		this.state = Vue.observable({list: null, map: null})
		watch(
			() => Locations.main,
			location => {
				this.state.map = null
				if (location !== null)
					this.updateList(location.id)
			},
			true
		)
	}

	public canPreapprove(organizationId: number): boolean {
		if (!this.isReady)
			return true

		const subscription = this.map.get(organizationId)

		if (subscription === undefined) {
			Notifications.warning(`Content subscription not found, id: ${organizationId}`)
			return false
		}
		return subscription.allowPreApproved
	}

	public update(): Promise<void> {
		return this.updateList(Locations.main!.id)
	}

	public subscribeMain(ticket: string, allowPreApproved: boolean): Promise<void> {
		return this.subscribe(Locations.main!.id, ticket, allowPreApproved)
	}

	public async subscribe(locationId: number, ticket: string, allowPreApproved: boolean): Promise<void> {
		await Portal.call.locations.contentSubscriptionsPost(locationId, ticket, allowPreApproved).response

		if (locationId === Locations.main?.id)
			await this.updateList(locationId)
	}

	public async modifyAllowPreApprovedMain(organizationId: number, allowPreApproved: boolean): Promise<void> {
		const locationId = Locations.main!.id

		await Portal.call.locations.contentSubscriptionsPut(locationId, organizationId, allowPreApproved).response

		if (locationId !== Locations.main?.id)
			return

		if (this.state.map !== null) {
			const subscription = this.state.map.get(organizationId)
			if (subscription !== undefined)
				subscription.allowPreApproved = allowPreApproved
		}
	}

	public async deleteMain(organizationId: number): Promise<void> {
		const locationId = Locations.main!.id
		await Portal.call.organizations.contentSubscribersDelete(organizationId, locationId).response

		if (locationId !== Locations.main?.id)
			return

		if(this.state.map !== null)
			this.state.map.delete(organizationId)
	}

	private async updateList(locationId: number): Promise<void> {
		const data = await Portal.call.locations.contentSubscriptionsGet(locationId).response

		if (locationId !== Locations.main?.id)
			return

		const list = data
			.map(subscription => ContentSubscription.fromApi(subscription))

		this.state.map = new Map(list.map(subscription => [subscription.id, subscription]))
	}
}

export {ContentSubscription}
export default new ContentSubscriptions()
